<template>
  <select
    v-bind="computedAttrs"
    class="py-2 pl-3 pr-8 border border-gray-200 border-solid rounded-md leading-default outline-0 w-full appearance-none appearance-arrow"
    v-model="value"
    @change="onSelectChange"
    :class="{'cursor-not-allowed': disabled}"
  >
    <option 
      v-for="(option, index) in options" 
      :key="option[optionKey]?.toString() ?? index"
      :value="optionValue === null ? option : option[optionValue]"
    >
      <slot :option="option" :index="index" />
    </option>
  </select>
</template>

<style scoped>
.appearance-arrow {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23727c8f' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
}
</style>

<script setup lang="ts" generic="T, K extends keyof T">
import { computed } from 'vue';

interface Props<T, K> {
  id?: string;
  disabled?: boolean;
  options: ReadonlyArray<T>;
  optionKey: K;
  optionValue?: K | null;
}

interface Emits<T> {
  (e: 'change', value: T | null): void;
}

defineSlots<{
  default?: (props: { option: T, index: number }) => T
}>();

const { id = undefined, disabled, optionValue = null } = defineProps<Props<T, K>>();
const emit = defineEmits<Emits<T>>();
const value = defineModel<T | null>({ default: null });

const computedAttrs = computed(() => ({
  'aria-disabled': disabled,
  'disabled': disabled,
  'id': id,
}));

const onSelectChange = () => emit('change', value.value);
</script>